<script setup>
import { ref } from 'vue'
import { useMusic } from 'src/composables/useMusic.js'

const {
  volume,
  muted,
  volumeSet,
  toggleMute
} = useMusic();

const slider = ref(null);

function onSliderDown(e) {
  const el = slider.value;
  if (!el) return;

  let dragging = true;

  const update = (ev) => {
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(ev.clientX - rect.left, rect.width));
    volumeSet(x / rect.width);
  }

  const move = (ev) => dragging && update(ev);
  const up = () => {
    dragging = false;
    document.removeEventListener('mousemove', move);
    document.removeEventListener('mouseup', up);
  }

  update(e)
  document.addEventListener('mousemove', move);
  document.addEventListener('mouseup', up);
}
</script>

<template>
  <div class="audio-controls">
    <label class="mute-button">
      <input class="music-mute" type="checkbox" :checked="muted" @change="toggleMute"/>
      <span class="mute-check" title="Mute music"></span>
    </label>
    <div class="volume-slider" ref="slider" @mousedown.prevent="onSliderDown">
      <div class="volume-track">
        <div class="volume-fill" :style="{ width: volume * 100 + '%' }"></div>
      </div>
      <div class="volume-thumb" :style="{ left: volume * 100 + '%' }"></div>
    </div>
    <button class="volume-button-dwn button-default" @click="volumeSet(volume - 0.1)"></button>
    <button class="volume-button-up button-default" @click="volumeSet(volume + 0.1)"></button>
    <div class="volume-percentage">{{ Math.round(volume * 100) + '%' }}</div>
  </div>
</template>

<style lang="scss">
@import '../../_variables.scss';

.audio-controls {
  border-radius: var(--panelRadius);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  min-height: 36px;
  max-height: 36px;

  .mute-button {
    position: relative;
    display: inline-block;
    cursor: pointer;
    border-radius: var(--panelRadius);
    transition: background-color 0.2s;
    flex: 0 0 27px;
    margin-left: 2px;
  }
  
  .mute-button input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .mute-check {
    display: flex;
    font-family: "Arimo";
    font-size: 20px;
    text-align: center;
    color: var(--lightText);
    width: 24px;
    height: 24px;
    border-radius: 100%;
    align-items: center;
    justify-content: center;
    padding: 4px;
    margin: -4px;
  }
  
  /* unmuted state */
  .mute-check::before {
    content: '';
  }
  
  /* muted state */
  input:checked ~ .mute-check::before {
    content: '';
    color: var(--icon);
  }
  
  .mute-check:hover {
    background-color: color-mix(in srgb, var(--icon) 40%, #00000000);
  }
  input:checked ~ .mute-check:hover::before {
    color: var(--lightText);
  }
  
  .volume-slider {
    position: relative;
    display: inline-block;
    width: 100%;
    height: 20px;
    vertical-align: middle;
    cursor: pointer;
    margin-left: 10px;
    margin-right: 10px;
  }
  
  .volume-track {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    height: 4px;
    background-color: var(--btn);
    border-radius: 2px;
    user-select: none;
  }
  
  .volume-fill {
    position: absolute;
    height: 100%;
    width: 20%;
    background-color: var(--selectedMenuFaintLink);
    border-radius: 2px;
  }
  
  .volume-thumb {
    position: absolute;
    top: 50%;
    left: 20%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background-color: var(--link);
    border-radius: 50%;
    user-select: none;
  }
  
  .volume-button-dwn, .volume-button-up {
    display: unset;
    font-family: "Arimo" !important;
    font-size: 16px;
    text-align: center;
    color: var(--icon);
    width: 24px;
    height: 25px;
    flex: 0 0 24px;
  }
  
  .volume-button-up:hover, .volume-button-dwn:hover {
    color: var(--lightText);
  }
  
  .volume-percentage {
    width: 32px;
    flex: 0 0 32px;
    user-select: none;
  }
}
</style>
